<template>
  <div class="lic-page">
    <section class="lic-hero">
      <div class="lic-wrap">
        <div class="lic-badge">Administration</div>
        <h1 class="lic-title">Licences</h1>
        <p class="lic-subtitle">Issue a licence and keep the record of every one issued.</p>
      </div>
    </section>

    <section class="lic-body">
      <div class="lic-wrap">

        <p v-if="checking" class="lic-note">Checking…</p>

        <div v-else-if="!isAdmin" class="lic-card lic-denied">
          <h2 class="lic-h2">Not available on this account</h2>
          <p>Licences are issued by administrators. If you are signed in and expected to see this,
             ask for the administrator role to be added to your account.</p>
        </div>

        <template v-else>
          <div class="lic-card">
            <h2 class="lic-h2">Issue a licence</h2>
            <form @submit.prevent="issue" class="lic-form">
              <div class="lic-row">
                <div class="lic-group">
                  <label class="lic-label">Customer *</label>
                  <input v-model="form.customer" type="text" class="lic-input" required
                         placeholder="Acme Corp" maxlength="200" />
                </div>
                <div class="lic-group">
                  <label class="lic-label">Tier *</label>
                  <select v-model="form.tier" class="lic-input">
                    <option value="enterprise">Enterprise</option>
                    <option value="consumer">Consumer</option>
                  </select>
                </div>
              </div>
              <div class="lic-row">
                <div class="lic-group">
                  <label class="lic-label">Devices *</label>
                  <input v-model="form.max_devices" type="number" class="lic-input" required
                         min="1" max="100000" />
                </div>
                <div class="lic-group">
                  <label class="lic-label">Valid from *</label>
                  <input v-model="form.valid_from" type="date" class="lic-input" required />
                </div>
                <div class="lic-group">
                  <label class="lic-label">Valid until *</label>
                  <input v-model="form.valid_until" type="date" class="lic-input" required />
                </div>
              </div>
              <div class="lic-row">
                <div class="lic-group">
                  <label class="lic-label">Grace days</label>
                  <input v-model="form.grace_days" type="number" class="lic-input" min="0" max="3650"
                         :placeholder="form.tier === 'enterprise' ? '7 if left blank' : '0 if left blank'" />
                </div>
                <div class="lic-group">
                  <label class="lic-label">Hard stop days</label>
                  <input v-model="form.hard_stop_days" type="number" class="lic-input" min="0" max="3650"
                         :placeholder="form.tier === 'enterprise' ? '30 if left blank' : '7 if left blank'" />
                </div>
                <div class="lic-group lic-grow">
                  <label class="lic-label">Note</label>
                  <input v-model="form.note" type="text" class="lic-input" maxlength="1000"
                         placeholder="Kept here only — not part of the licence" />
                </div>
              </div>
              <button type="submit" class="lic-btn" :disabled="issuing">
                {{ issuing ? "Signing…" : "Issue licence" }}
              </button>
              <p v-if="error" class="lic-error">{{ error }}</p>
            </form>
          </div>

          <div v-if="issued" class="lic-card lic-issued">
            <h2 class="lic-h2">Issued</h2>
            <p class="lic-note" v-if="issued.recorded === false">
              This licence is signed and valid, but the record of it could not be written.
              Copy the token now — it cannot be produced again.
            </p>
            <p>{{ issued.licence.customer }} — {{ issued.licence.tier }},
               {{ issued.licence.max_devices }} devices, until {{ day(issued.licence.valid_until) }}.</p>
            <textarea class="lic-token" readonly rows="4" :value="issued.token"></textarea>
            <button class="lic-btn lic-btn-quiet" @click="copy(issued.token)">
              {{ copied === issued.token ? "Copied" : "Copy token" }}
            </button>
          </div>

          <div class="lic-card">
            <h2 class="lic-h2">Issued licences <span class="lic-count">{{ licences.length }}</span></h2>
            <p v-if="loading" class="lic-note">Loading…</p>
            <p v-else-if="!licences.length" class="lic-note">None yet.</p>
            <div v-else class="lic-list">
              <div v-for="l in licences" :key="l.lid" class="lic-item" :class="{'lic-dead': l.revoked}">
                <div class="lic-item-head">
                  <strong>{{ l.customer }}</strong>
                  <span class="lic-tag">{{ l.tier }}</span>
                  <span class="lic-tag">{{ l.max_devices }} devices</span>
                  <span class="lic-tag" :class="stateClass(l)">{{ state(l) }}</span>
                </div>
                <div class="lic-item-meta">
                  {{ day(l.valid_from) }} → {{ day(l.valid_until) }}
                  · issued {{ day(l.issued_at) }}<span v-if="l.issued_by_email"> by {{ l.issued_by_email }}</span>
                  · <span class="lic-mono">{{ l.lid }}</span>
                </div>
                <div v-if="l.note" class="lic-item-note">{{ l.note }}</div>
                <div class="lic-item-actions">
                  <button class="lic-btn lic-btn-quiet" @click="copy(l.token)">
                    {{ copied === l.token ? "Copied" : "Copy token" }}
                  </button>
                  <button class="lic-btn lic-btn-quiet" @click="setRevoked(l, !l.revoked)">
                    {{ l.revoked ? "Reinstate" : "Mark revoked" }}
                  </button>
                </div>
              </div>
            </div>
            <p class="lic-note lic-small">
              Marking a licence revoked is this site's record of the decision. A queen already
              holding the token keeps working until it expires — the enterprise check is offline
              and has nothing to ask.
            </p>
          </div>
        </template>

      </div>
    </section>
  </div>
</template>
<script>
import {onMounted, reactive, ref} from "vue";
import Axios from "axios";

const api = (path) => (process.env.VUE_APP_SERVER_URI || "/") + path;

export default {
    "name": "Licences",
    setup () {

        const form = reactive({
            "customer": "",
            "tier": "enterprise",
            "max_devices": 10,
            "valid_from": new Date().toISOString().slice(0, 10),
            "valid_until": "",
            "grace_days": "",
            "hard_stop_days": "",
            "note": ""
        });
        const licences = ref([]);
        const isAdmin = ref(false);
        const checking = ref(true);
        const loading = ref(false);
        const issuing = ref(false);
        const issued = ref(null);
        const error = ref("");
        const copied = ref("");

        // The cookie is what says who is asking, and the site is served from one
        // origin, so it travels on its own. Said here because a request that
        // quietly loses it reads as "not an administrator" rather than as an
        // error.
        Axios.defaults.withCredentials = true;

        function day (seconds) {

            if (!seconds) { return "—"; }
            return new Date(seconds * 1000).toISOString().slice(0, 10);

        }

        function state (l) {

            if (l.revoked) { return "revoked"; }
            const now = Math.floor(Date.now() / 1000);
            if (now < l.valid_from) { return "not yet started"; }
            if (now > l.valid_until) { return "expired"; }
            return "active";

        }

        function stateClass (l) {

            const s = state(l);
            return {"lic-ok": s === "active", "lic-warn": s === "expired" || s === "revoked"};

        }

        async function load () {

            loading.value = true;
            try {

                const r = await Axios.get(api("api/licences"));
                licences.value = (r.data && r.data.licences) || [];

            } catch (e) {

                error.value = "Could not read the licences.";

            }
            loading.value = false;

        }

        async function issue () {

            issuing.value = true;
            error.value = "";
            issued.value = null;
            try {

                const r = await Axios.post(api("api/licences"), {...form});
                if (r.data && r.data.error) {

                    error.value = r.data.error;

                } else if (r.data && r.data.token) {

                    issued.value = {"token": r.data.token, "licence": r.data.licence, "recorded": r.data.recorded};
                    form.customer = "";
                    form.note = "";
                    await load();

                } else {

                    error.value = "The licence was not issued and the site did not say why.";

                }

            } catch (e) {

                error.value = "The licence was not issued. You may have been signed out.";

            }
            issuing.value = false;

        }

        async function setRevoked (l, revoked) {

            try {

                await Axios.post(api("api/licences/revoke"), {"lid": l.lid, revoked});
                await load();

            } catch (e) {

                error.value = "That did not save.";

            }

        }

        async function copy (text) {

            try {

                await navigator.clipboard.writeText(text);
                copied.value = text;
                setTimeout(() => { copied.value = ""; }, 2000);

            } catch (e) {

                error.value = "Could not reach the clipboard — select the token and copy it.";

            }

        }

        onMounted(async () => {

            try {

                const r = await Axios.get(api("api/amIAdmin"));
                isAdmin.value = !!(r.data && r.data.admin);

            } catch (e) {

                isAdmin.value = false;

            }
            checking.value = false;
            if (isAdmin.value) { await load(); }

        });

        return {checking, copied, copy, day, error, form, isAdmin, issue, issued, issuing,
            licences, loading, setRevoked, state, stateClass};

    }
};
</script>
<style scoped>
.lic-page { font-family: var(--font-body); color: var(--text-primary); background: var(--bg-primary); font-size: 17px; }
.lic-wrap { max-width: 1100px; margin: 0 auto; padding: 0 24px; }
.lic-hero { padding: 96px 0 24px; }
.lic-badge {
  display: inline-block; padding: 6px 14px; margin-bottom: 16px; border-radius: 999px;
  font-family: var(--font-heading); font-size: 0.72em; font-weight: 600;
  letter-spacing: 0.12em; text-transform: uppercase;
  color: var(--accent-purple); background: rgba(124, 58, 237, 0.1);
  border: 1px solid rgba(124, 58, 237, 0.2);
}
.lic-title {
  font-family: var(--font-heading); font-size: 2.6em; font-weight: 700;
  letter-spacing: -1px; margin: 0 0 10px;
  background: var(--gradient-text, var(--gradient-primary));
  -webkit-background-clip: text; background-clip: text;
  -webkit-text-fill-color: transparent;
}
.lic-subtitle { color: var(--text-secondary); margin: 0; }
.lic-body { padding: 24px 0 120px; }

.lic-card {
  background: var(--bg-card); border: 1px solid var(--border-subtle);
  border-radius: var(--radius-lg); padding: 28px; margin-bottom: 24px;
  box-shadow: var(--shadow-md);
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
}
.lic-card:hover { border-color: var(--border-hover); }
.lic-h2 {
  font-family: var(--font-heading); font-size: 1.2em; font-weight: 700;
  margin: 0 0 20px; color: var(--text-primary);
}
.lic-count { font-size: 0.62em; font-weight: 500; color: var(--text-muted); margin-left: 8px; }

.lic-row { display: flex; gap: 18px; flex-wrap: wrap; margin-bottom: 18px; }
.lic-group { display: flex; flex-direction: column; gap: 7px; min-width: 190px; }
.lic-grow { flex: 1 1 260px; }
.lic-label {
  font-family: var(--font-heading); font-size: 0.72em; font-weight: 600;
  letter-spacing: 0.08em; text-transform: uppercase; color: var(--text-secondary);
}
.lic-input {
  padding: 13px 16px; font-family: var(--font-body); font-size: 0.98em;
  border: 2px solid rgba(124, 58, 237, 0.12); border-radius: 12px;
  background: rgba(124, 58, 237, 0.04); color: var(--text-primary); outline: none;
  transition: border-color 0.25s ease, box-shadow 0.25s ease, background 0.25s ease;
}
.lic-input:hover { border-color: rgba(124, 58, 237, 0.24); }
.lic-input:focus {
  border-color: var(--accent-purple); background: rgba(124, 58, 237, 0.06);
  box-shadow: 0 0 0 4px rgba(124, 58, 237, 0.12);
}

.lic-btn {
  padding: 13px 30px; border: 2px solid transparent; border-radius: 30px;
  font-family: var(--font-heading); font-size: 0.9em; font-weight: 700;
  color: #fff; cursor: pointer;
  background: var(--gradient-primary);
  box-shadow: 0 8px 28px rgba(99, 102, 241, 0.3);
  transition: transform 0.3s ease, box-shadow 0.3s ease, background 0.3s ease, color 0.3s ease;
}
.lic-btn:hover { transform: translateY(-2px); box-shadow: 0 14px 40px rgba(6, 182, 212, 0.4); }
.lic-btn:active { transform: translateY(0); }
.lic-btn[disabled] { opacity: 0.55; cursor: default; transform: none; box-shadow: none; }
.lic-btn-quiet {
  padding: 9px 20px; font-size: 0.82em;
  background: transparent; color: var(--text-secondary);
  border-color: var(--border-subtle); box-shadow: none;
}
.lic-btn-quiet:hover {
  transform: translateY(-1px); color: var(--accent-purple);
  border-color: var(--border-hover); background: rgba(124, 58, 237, 0.06);
  box-shadow: 0 6px 20px rgba(124, 58, 237, 0.14);
}

.lic-error { color: #e2565f; margin: 14px 0 0; font-size: 0.92em; }
.lic-note { color: var(--text-secondary); }
.lic-small { font-size: 0.85em; margin-top: 20px; line-height: 1.6; }
.lic-token {
  width: 100%; padding: 14px; margin: 14px 0; resize: vertical;
  font-family: var(--font-mono); font-size: 0.72em; line-height: 1.5; word-break: break-all;
  border: 2px solid rgba(6, 182, 212, 0.2); border-radius: 12px;
  background: rgba(6, 182, 212, 0.05); color: var(--text-primary); outline: none;
  transition: border-color 0.25s ease;
}
.lic-token:focus { border-color: var(--accent-cyan); }
.lic-issued { border-color: rgba(6, 182, 212, 0.35); }

.lic-list { display: flex; flex-direction: column; gap: 14px; }
.lic-item {
  border: 1px solid var(--border-subtle); border-radius: var(--radius-md);
  padding: 16px 18px; background: var(--bg-secondary);
  transition: border-color 0.25s ease, transform 0.25s ease, box-shadow 0.25s ease;
}
.lic-item:hover {
  border-color: var(--border-hover); transform: translateY(-2px);
  box-shadow: 0 10px 30px rgba(124, 58, 237, 0.1);
}
.lic-dead { opacity: 0.55; }
.lic-item-head { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.lic-tag {
  font-family: var(--font-heading); font-size: 0.68em; font-weight: 600;
  letter-spacing: 0.04em; text-transform: uppercase;
  padding: 4px 10px; border-radius: 999px;
  border: 1px solid var(--border-subtle); color: var(--text-secondary);
}
.lic-ok { color: #10b981; border-color: rgba(16, 185, 129, 0.35); background: rgba(16, 185, 129, 0.08); }
.lic-warn { color: #f59e0b; border-color: rgba(245, 158, 11, 0.35); background: rgba(245, 158, 11, 0.08); }
.lic-item-meta { font-size: 0.8em; color: var(--text-muted); margin-top: 8px; line-height: 1.6; }
.lic-item-note { font-size: 0.88em; color: var(--text-secondary); margin-top: 10px; }
.lic-item-actions { display: flex; gap: 10px; margin-top: 14px; flex-wrap: wrap; }
.lic-mono { font-family: var(--font-mono); font-size: 0.95em; }
.lic-denied { max-width: 640px; }
.lic-denied p { color: var(--text-secondary); line-height: 1.6; margin: 0; }

@media (max-width: 720px) {
  .lic-title { font-size: 1.9em; }
  .lic-hero { padding: 76px 0 16px; }
  .lic-row { flex-direction: column; gap: 14px; }
  .lic-card { padding: 20px; }
}
</style>
